import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useQuery,
  keepPreviousData,
  queryOptions,
} from '@tanstack/react-query';
import { useAtomValue, useSetAtom } from 'jotai';
import { useParams } from 'react-router-dom';
import {
  ltdRefreshTriggerAtom,
  unitVerificationsAtom,
  inventoryVerificationSummaryAtom,
  type TypeCurveHeadersQueryParams,
} from './atoms';
import type { SortingState } from '@tanstack/react-table';
import {
  TypeCurveHeader,
  VolumeRecord,
  RatioRecord,
  TypeCurveVolumeRecord,
  TypeCurveRatioRecord,
  DevelopmentArea,
  DevelopmentAreasQueryResponse,
  FormationSlot,
  FormationSlotsQueryResponse,
} from 'types/api-responses';
import request from 'lib/request';
import { api } from 'lib/request/api';
import { HTTPError } from 'ky';
import { useServerExport, ExportParams } from 'lib/hooks/useServerExport';
import {
  postSpatialExport,
  useSpatialExport,
} from 'lib/hooks/use-spatial-export';
import type { SingleLayerSpatialExportRequest } from 'components/export-spatial-data';
import type { MultiWellProductionData } from 'components/charts/multi-well-volume-chart';
import type {
  FilteredWell,
  TcMapFilterMode,
  TcUnitFilters,
  TcWellsQueryPayload,
  TcWellsQueryResponse,
  TypeCurveWellRow,
  UnitVerification,
  UnitVerificationsResponse,
  InventoryVerificationSummary,
} from './types';
import {
  pageSectionVisibilityAtom,
  tcWellsDataAtom,
  tcWellsFacetsAtom,
  tcWellFiltersAtom,
  tcEnabledWellFiltersAtom,
  tcInventoryWellScopeAtom,
  tcWellsStatusCountsAtom,
  tcEffectiveMapFilterModeAtom,
  tcMapBoundsAtom,
  tcAoiPolygonAtom,
  tcAoiPolygonLockedAtom,
  tcColorByUwiMapAtom,
  tcColorByRulesAtom,
  tcFilteredUwisAtom,
  tcWellsTotalCountAtom,
  tcWellsLoadingAtom,
  tcUnitFiltersAtom,
  tcDisabledUnitFilterCategoriesAtom,
  tcUnitsFacetsAtom,
  tcUnitsTotalCountAtom,
  tcUnitsLoadingAtom,
  tcFilteredUnitsAtom,
  tcOverlappingUnitsAtom,
  tcSelectedDevAreaAtom,
  tcUnitResolutionRefreshAtom,
  tcUnitResolutionPendingAtom,
  tcUnitDetailsByIdAtom,
  tcUnitColorByRulesAtom,
  tcUnitColorByNameMapAtom,
  tcInventoryGunBarrelScopeAtom,
  tcHighlightedFormationAtom,
  type TcUnitDetail,
} from './type-curve-filters/atoms';
import { slotTableStateAtoms } from './inventory-surface/atoms';
import { useSurface } from './inventory-surface/surface';
import { selectedDcaScenarioAtom } from 'components/well-page-content/atoms';
import {
  activeTcWorkspaceAtom,
  latestPromotedWorkspaceIdAtom,
  tcEffectiveWorkspaceIdAtom,
} from 'components/type-curve-workspace/atoms';
import { buildTcWellsQueryPayload } from './build-tc-wells-query-payload';
import { buildDevelopmentAreasPayload } from './build-development-areas-payload';
import { buildFormationSlotsPayload } from './build-formation-slots-payload';
import type {
  CrossPlotData,
  CrossPlotDataPoint,
  CrossPlotApiResponse,
} from 'types/type-curve-cross-plot';

import type { TableState } from 'components/data-table/types';
import type {
  TypeCurvesListingFilters,
  TypeCurvesListingFacets,
} from './use-type-curves-listing';
import { mapColumnIdToSortAttribute } from './utils';
import {
  DEFAULT_VERIFICATION_STATUS_RULE,
  DEFAULT_VERIFICATION_STATUS_RULE_ID,
  DEFAULT_WELL_QUALIFIER_RULE,
  TC_UNIT_FILTER_CATEGORIES,
  omitDisabledCategories,
} from './type-curve-filters/utils';

// ─── Shared Listing Table State ───

/**
 * Rows per page on all four listing tabs.
 *
 * Single source of truth: {@link useListingTableState} seeds `pageSize` from it
 * and `type-curves-content.tsx` seeds the DataTable's initial state from it. If
 * the two drift, the table shows one page size while the request asks for another.
 */
export const DEFAULT_PAGE_SIZE = 100;

/** The table-state slice each listing tab spreads into its own hook return. */
export interface ListingTableProps {
  pagination: { page: number; pageSize: number };
  /**
   * The active global search text, so the listing map can fold it into the
   * filter payload it sends the tile (the map draws the searched set too).
   */
  search: string;
  onPaginationChange: (state: TableState) => void;
  onSortChange: (state: TableState) => void;
  onGlobalSearchChange: (state: TableState) => void;
}

/**
 * Page / sort / search state shared by the four Type Curve listing tabs
 * (Type Curves, Workspaces, Scenarios, Inventories). Each tab builds its own
 * query from `page`/`pageSize`/`sort`/`search` and spreads `tableProps` into its
 * return, so the paginator / sort / search boilerplate lives here once instead of
 * being copied per tab (lint:dupes).
 *
 * @return {object} The raw `page`/`pageSize`/`sort`/`search` for the query, plus a
 *   `tableProps` bundle (`pagination`, `search`, and the three change handlers).
 */
export function useListingTableState() {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [sort, setSort] = useState<{ id: string; desc: boolean } | null>(null);
  const [search, setSearch] = useState('');

  const onPaginationChange = useCallback((state: TableState) => {
    setPage(state.page);
    setPageSize(state.pageSize);
  }, []);

  const onSortChange = useCallback((state: TableState) => {
    setSort(state.sorting[0] ?? null);
    setPage(state.page);
  }, []);

  const onGlobalSearchChange = useCallback((state: TableState) => {
    setSearch(state.globalFilter);
    setPage(1);
  }, []);

  const tableProps: ListingTableProps = {
    pagination: { page, pageSize },
    search,
    onPaginationChange,
    onSortChange,
    onGlobalSearchChange,
  };

  return { page, pageSize, sort, search, tableProps };
}

/** The common result shape every listing tab returns, before any row remap. */
export interface ListingQueryResult<TRow> {
  rows: TRow[];
  totalCount: number;
  facets?: TypeCurvesListingFacets;
  isFetching: boolean;
  error: string | null;
  /**
   * `true` once the first response has landed — `totalCount` reads 0 both before
   * the first fetch and for a genuinely empty result, so consumers that must tell
   * "loading" from "no matches" (the Export dropdown) read this instead.
   */
  hasLoaded: boolean;
  /**
   * The table-state part of the request this hook posts: the search text and the sort, encoded
   * exactly as the listing query sends them. The page spreads this into the export params so an
   * export is scoped and ordered like the table it was taken from.
   */
  requestParams: ListingRequestParams;
}

/** The search text and sort the listing query posts, in the shape the API takes. */
export interface ListingRequestParams {
  text?: string;
  sortAttribute?: string;
  sortDirection?: 'ASC' | 'DESC';
}

interface ListingQueryResponse<TRow> {
  totalCount: number;
  results: TRow[];
  facets?: TypeCurvesListingFacets;
}

/**
 * The single listing query behind the four Type Curve tabs. Each tab is the same
 * `POST queries/type_curve_headers` with a different `view` discriminator and React
 * Query key; this runs it with the shared page / sort / search state and returns
 * the common result shape plus the table handlers, so the query + paginator
 * boilerplate lives here once instead of being copied per tab (lint:dupes). A tab
 * that reshapes its rows (e.g. Scenarios) maps `rows` on the returned value.
 *
 * @param {string} view - The listing view discriminator sent to the endpoint.
 * @param {string} queryKeyPrefix - This tab's React Query cache-key prefix.
 * @param {TypeCurvesListingFilters} filters - Shared filter-panel state.
 * @param {{ enabled: boolean }} options - Only the active tab fetches.
 * @return {ListingQueryResult<TRow> & ListingTableProps} Rows, count, facets,
 *   status and the shared table handlers.
 */
export function useListingQuery<TRow>(
  view: 'type_curves' | 'workspaces' | 'scenarios' | 'inventories',
  queryKeyPrefix: string,
  filters: TypeCurvesListingFilters,
  options: { enabled: boolean }
): ListingQueryResult<TRow> & ListingTableProps {
  const { page, pageSize, sort, search, tableProps } = useListingTableState();

  // One encoding of the table state, shared by the request below and by the export (via the
  // return value), so the two cannot drift.
  const requestParams = useMemo<ListingRequestParams>(
    () => ({
      text: search || undefined,
      sortAttribute: sort?.id,
      sortDirection: sort ? (sort.desc ? 'DESC' : 'ASC') : undefined,
    }),
    [search, sort]
  );

  const query = useQuery(
    queryOptions({
      // `view` is in the key as well as the prefix: the prefix already differs per tab, but the linter
      // (and correctness) wants every value the queryFn reads to key the cache.
      queryKey: [
        queryKeyPrefix,
        view,
        filters,
        { page, pageSize, requestParams },
      ],
      queryFn: ({ signal }) =>
        api
          .post('queries/type_curve_headers', {
            json: {
              query: {
                view,
                ...filters,
                page,
                pageSize,
                ...requestParams,
              },
            },
            signal,
          })
          .json<ListingQueryResponse<TRow>>(),
      enabled: options.enabled,
      placeholderData: keepPreviousData,
      staleTime: 300_000,
    })
  );

  return {
    rows: query.data?.results ?? [],
    totalCount: query.data?.totalCount ?? 0,
    facets: query.data?.facets,
    isFetching: query.isFetching,
    error: query.error ? String(query.error) : null,
    hasLoaded: query.data !== undefined,
    requestParams,
    ...tableProps,
  };
}

const QUERY_PATH = '/queries/wells';

// Inventory Workspace overrides, applied to a built wells payload.
//
// Two surfaces on the Inventory page have to resolve the same well set as the
// zone's slots, and the payload builder's Type Curve defaults do not: it bounds
// by the Type Curve Area polygon matched against the whole directional survey.
// The units neither tile the area nor stay inside it, so the defaults counted
// wells no unit contains and missed wells the inventory holds.
//
// The zone's unit footprint enters one of two ways, because the Global Map Filter
// means something different in each mode:
//
//   'type-curve-area' — the area polygon is the thing that is wrong, so replace
//     it outright. The footprint IS the bound.
//   visible extent / drawn AOI — the operator has deliberately narrowed the view,
//     which is a real thing to want, so keep their shape and send the footprint
//     as a hard AND ceiling. They narrow within the inventory, never outside it.
//     (One drawn AOI returned 412 wells against a 119-well zone; intersected, 119.)
//
// `scope` null (the plain Type Curve pages) leaves the payload untouched.
function applyInventoryWellScope(
  payload: TcWellsQueryPayload,
  scope: { unitScopeGeojson: GeoJSON.MultiPolygon | GeoJSON.Polygon } | null,
  mapFilterMode: TcMapFilterMode
): void {
  if (!scope) return;
  payload.spatialMatchOnLateral = true;
  // Not every wellbore carries an estimated lateral; lateral-only matching drops
  // the ones that don't, and the survey is the only geometry they have.
  payload.spatialLateralSurveyFallback = true;
  if (mapFilterMode === 'type-curve-area') {
    delete payload.polygonEwkb;
    payload.polygonGeojson = scope.unitScopeGeojson;
    return;
  }
  payload.inventoryUnitScopeGeojson = scope.unitScopeGeojson;
}

// ─── Table Hook (server-side paginated) ───

export const useTcTableWellsQuery = (typeCurveHeader: TypeCurveHeader) => {
  const selectedDcaScenario = useAtomValue(selectedDcaScenarioAtom);
  const filters = useAtomValue(tcWellFiltersAtom);
  const setFilters = useSetAtom(tcWellFiltersAtom);
  // Disabled filters keep their saved row but must not narrow the query.
  const enabledFilters = useAtomValue(tcEnabledWellFiltersAtom);
  const mapFilterMode = useAtomValue(tcEffectiveMapFilterModeAtom);
  const mapBounds = useAtomValue(tcMapBoundsAtom);
  const aoiPolygon = useAtomValue(tcAoiPolygonAtom);
  const aoiLocked = useAtomValue(tcAoiPolygonLockedAtom);
  // Inventory Workspace only: bound by the zone's units, matched on the lateral.
  const inventoryWellScope = useAtomValue(tcInventoryWellScopeAtom);

  const setFacets = useSetAtom(tcWellsFacetsAtom);
  const setStatusCounts = useSetAtom(tcWellsStatusCountsAtom);
  const setFilteredUwis = useSetAtom(tcFilteredUwisAtom);
  const setSharedWellsData = useSetAtom(tcWellsDataAtom);
  const setColorByUwiMap = useSetAtom(tcColorByUwiMapAtom);
  const setSharedTotalCount = useSetAtom(tcWellsTotalCountAtom);
  const setSharedLoading = useSetAtom(tcWellsLoadingAtom);
  const colorByRules = useAtomValue(tcColorByRulesAtom);
  const colorByRulesRef = useRef(colorByRules);
  colorByRulesRef.current = colorByRules;

  const [wells, setWells] = useState<TypeCurveWellRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // Seed default filters from header on first mount
  const hasSeededRef = useRef(false);
  // Set when a seed that WILL change `enabledFilters` is pending, so the initial
  // fetch can suppress its pre-seed run and fire once with the seeded defaults
  // (avoids the mount double-fetch). Stays false on a warm start / empty defaults.
  const seedPendingRef = useRef(false);
  useEffect(() => {
    if (hasSeededRef.current) return;
    hasSeededRef.current = true;
    if (Object.keys(filters).length > 0) return;
    const defaults: Record<string, string[] | undefined> = {};
    if (typeCurveHeader.developmentType) {
      defaults.holeDirection = [typeCurveHeader.developmentType.toUpperCase()];
    }
    if (typeCurveHeader.formation) {
      defaults.formation = [typeCurveHeader.formation];
    }
    if (Object.keys(defaults).length === 0) return;
    seedPendingRef.current = true;
    setFilters(defaults);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchTableWells = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sorting?: SortingState;
      text?: string;
    }) => {
      // Empty state: no Type Curve Area on the header — make no wells call and
      // clear shared results so downstream charts (probit/cross-plot/volumes,
      // which key off filteredUwis) stay silent too.
      if (typeCurveHeader.typeCurveAreaId === null) {
        setWells([]);
        setTotalCount(0);
        setFilteredUwis([]);
        setSharedWellsData([]);
        setSharedTotalCount(0);
        setIsLoading(false);
        setSharedLoading(false);
        return;
      }
      if (!selectedDcaScenario?.id) return;
      // Only the shapes bound a tc_mode query, so a mode with no shape yet must
      // not fire: the server would match every producing well.
      if (mapFilterMode === 'type-curve-area' && !typeCurveHeader.geometry)
        return;
      if (mapFilterMode === 'visible-extent' && !mapBounds) return;
      if (mapFilterMode === 'aoi-polygon' && (!aoiPolygon || !aoiLocked))
        return;

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setSharedLoading(true);
      setError(null);

      const sortCol = params.sorting?.[0];
      const sortAttribute = sortCol
        ? mapColumnIdToSortAttribute(sortCol.id)
        : undefined;
      const sortDirection = sortCol
        ? sortCol.desc
          ? 'desc'
          : 'asc'
        : undefined;

      const payload = buildTcWellsQueryPayload({
        typeCurveHeader,
        dcaScenarioId: selectedDcaScenario.id,
        filters: enabledFilters,
        mapFilterMode,
        mapBounds,
        aoiPolygon,
        page: params.page,
        pageSize: params.pageSize,
        limit: params.pageSize,
        sortAttribute,
        sortDirection,
        text: params.text || undefined,
      });

      applyInventoryWellScope(payload, inventoryWellScope, mapFilterMode);

      try {
        const response = await request.post<TcWellsQueryResponse, unknown>(
          QUERY_PATH,
          { query: payload },
          controller
        );

        if (controller.signal.aborted) return;
        if (response.type === 'abort') return;
        if (response.type === 'fatal') {
          setError(response.message ?? 'Request failed');
          setWells([]);
          setTotalCount(0);
          return;
        }
        if (response.type === 'error') {
          setError('Failed to load wells');
          setWells([]);
          setTotalCount(0);
          return;
        }
        if (response.type !== 'success') return;

        const data = response.data;
        const rows = transformWells(data.results ?? [], typeCurveHeader);

        setWells(rows);
        setTotalCount(data.totalCount ?? 0);
        setSharedTotalCount(data.totalCount ?? 0);
        setFilteredUwis(data.filteredUwis ?? []);
        setSharedWellsData(rows);

        if (data.facets) {
          setFacets(data.facets);
        }
        // Always assigned, never conditionally: these counts describe THIS
        // response, so a response that carries none has to clear the previous
        // one rather than leave a stale breakdown beside a fresh total.
        //
        // Stamped with the formation filter that was actually sent, so a
        // consumer showing one zone can tell whether these describe that zone.
        setStatusCounts(
          data.statusCounts
            ? {
                ...data.statusCounts,
                formation: payload.formationIds?.[0] ?? null,
              }
            : null
        );

        // Build well qualifier UWI map for default color-by rule.
        // Use filteredUwis (all matching wells) instead of rows (paginated)
        // so every well gets the correct color on the map.
        const filteredUwisData = data.filteredUwis ?? [];
        const qualifierUwiMap: Record<string, string[]> = {};
        for (const fw of filteredUwisData) {
          if (fw.wellQualifier && fw.uwi) {
            const key = `wellQualifier:${fw.wellQualifier}`;
            if (!qualifierUwiMap[key]) qualifierUwiMap[key] = [];
            qualifierUwiMap[key].push(fw.uwi);
          }
        }
        setColorByUwiMap((prev) => {
          const preserved: Record<string, string[]> = {};
          for (const [key, value] of Object.entries(prev)) {
            if (!key.startsWith('wellQualifier:')) {
              preserved[key] = value;
            }
          }
          return { ...preserved, ...qualifierUwiMap };
        });

        // Non-wellQualifier color-by UWI entries are refreshed by a separate
        // color-keyed effect (below), so pagination/sort/search on the table no
        // longer re-fires that per-value fan-out.
      } catch {
        if (!controller.signal.aborted) {
          setError('Failed to load wells');
          setWells([]);
          setTotalCount(0);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setSharedLoading(false);
        }
      }
    },
    [
      typeCurveHeader,
      selectedDcaScenario?.id,
      enabledFilters,
      mapFilterMode,
      mapBounds,
      aoiPolygon,
      aoiLocked,
      inventoryWellScope,
      setFacets,
      setStatusCounts,
      setFilteredUwis,
      setSharedWellsData,
      setColorByUwiMap,
      setSharedTotalCount,
      setSharedLoading,
    ]
  );

  const loadData = useCallback(
    (params: TableState) => {
      void fetchTableWells({
        page: params.page,
        pageSize: params.pageSize,
        sorting: params.sorting,
        text: params.globalFilter || undefined,
      });
    },
    [fetchTableWells]
  );

  // On external filter atom changes, reset to page 1. On cold mount the seed
  // effect above sets `seedPendingRef`; suppress the pre-seed run here so the
  // table fetches once, with the seeded defaults, instead of twice.
  useEffect(() => {
    if (!selectedDcaScenario?.id) return;
    if (seedPendingRef.current) {
      if (Object.keys(enabledFilters).length === 0) return;
      seedPendingRef.current = false;
    }
    void fetchTableWells({ page: 1, pageSize: 100 });
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchTableWells, selectedDcaScenario?.id, enabledFilters]);

  // ─── Color-by UWI fan-out (non-wellQualifier rules) ───
  // Value-stable signature of the enabled non-wellQualifier rules: re-runs the
  // fan-out only when a rule's attribute or selected values change — NOT on a
  // recolor (valueColors) or on array-identity churn.
  const colorFanoutKey = useMemo(
    () =>
      JSON.stringify(
        colorByRules
          .filter(
            (r) =>
              r.enabled &&
              r.attribute !== 'wellQualifier' &&
              r.selectedValues.length > 0
          )
          .map((r) => [r.attribute, [...r.selectedValues].sort()])
      ),
    [colorByRules]
  );

  // Fan out one `pageSize:1` wells query per enabled non-wellQualifier color-by
  // value to collect its filteredUwis into the shared color map. Keyed on the
  // color-relevant inputs only (header/scenario/filters/spatial/rules), so it no
  // longer re-fires on table pagination/sort/search. Also syncs hydrated/seeded
  // rules (useColorByInit sets the rules atom but not the UWI map).
  const colorByAbortRef = useRef<AbortController | null>(null);
  useEffect(() => {
    if (typeCurveHeader.typeCurveAreaId === null) return;
    if (!selectedDcaScenario?.id) return;
    if (mapFilterMode === 'type-curve-area' && !typeCurveHeader.geometry)
      return;
    if (mapFilterMode === 'visible-extent' && !mapBounds) return;
    if (mapFilterMode === 'aoi-polygon' && (!aoiPolygon || !aoiLocked)) return;

    const nonQRules = colorByRulesRef.current.filter(
      (r) =>
        r.enabled &&
        r.attribute !== 'wellQualifier' &&
        r.selectedValues.length > 0
    );
    if (nonQRules.length === 0) return;

    if (colorByAbortRef.current) colorByAbortRef.current.abort();
    const controller = new AbortController();
    colorByAbortRef.current = controller;

    void (async () => {
      const nqMap: Record<string, string[]> = {};
      await Promise.all(
        nonQRules.flatMap((rule) =>
          rule.selectedValues.map(async (value) => {
            try {
              if (controller.signal.aborted) return;
              const cbPayload = buildTcWellsQueryPayload({
                typeCurveHeader,
                dcaScenarioId: selectedDcaScenario.id,
                filters: { ...enabledFilters, [rule.attribute]: [value] },
                mapFilterMode,
                mapBounds,
                aoiPolygon,
                page: 1,
                pageSize: 1,
                limit: 1,
              });
              applyInventoryWellScope(
                cbPayload,
                inventoryWellScope,
                mapFilterMode
              );
              const resp = await request.post<TcWellsQueryResponse, unknown>(
                QUERY_PATH,
                { query: cbPayload },
                controller
              );
              if (controller.signal.aborted) return;
              if (resp.type !== 'success') return;
              nqMap[`${rule.attribute}:${value}`] = (
                resp.data.filteredUwis ?? []
              ).map((w) => w.uwi);
            } catch {
              // Skip individual failures
            }
          })
        )
      );
      if (!controller.signal.aborted) {
        setColorByUwiMap((prev) => ({ ...prev, ...nqMap }));
      }
    })();

    return () => {
      controller.abort();
    };
  }, [
    typeCurveHeader,
    selectedDcaScenario?.id,
    enabledFilters,
    mapFilterMode,
    mapBounds,
    aoiPolygon,
    aoiLocked,
    inventoryWellScope,
    colorFanoutKey,
    setColorByUwiMap,
  ]);

  return { wells, totalCount, isLoading, error, loadData };
};

// ─── Shared Utilities ───

export function transformWells(
  results: any[],
  typeCurveHeader: TypeCurveHeader
): TypeCurveWellRow[] {
  if (!results || results.length === 0) return [];

  return results.map((well) => {
    const qualifier =
      well.wellQualifier ?? determineWellQualifier(well, typeCurveHeader);
    return {
      ...well,
      wellQualifier: qualifier,
    };
  });
}

export function determineWellQualifier(
  well: any,
  typeCurveHeader: TypeCurveHeader
): string {
  const isInControl = typeCurveHeader.analogWells?.some(
    (cw) => cw === well.uwi
  );

  if (isInControl) return 'Well Control';

  if (well.firstProductionDate && typeCurveHeader.createdAt) {
    const fpd = new Date(well.firstProductionDate);
    const dc = new Date(typeCurveHeader.createdAt);
    return fpd > dc ? 'New Wells' : 'Analog Wells';
  }

  return '--';
}

// ─── Production Volumes Hook ───

const PRODUCTION_VOLUMES_PATH = '/queries/reservoir/production_volumes';
const PRODUCTION_VOLUMES_BATCH_SIZE = 300;

interface ProductionVolumeEntry {
  lastProductionMonth: string;
  lastProductionDate: string;
  monthsFromFirstToLastProduction: number;
  monthsReported: number;
  productionVolumeVersionId: string;
  perforatedLateralLengthFt?: number;
  scalingApplied?: boolean;
  scalingFactor?: number;
  volumes: VolumeRecord[];
  cumulatives?: VolumeRecord[];
  ratios?: RatioRecord[];
  mbts?: VolumeRecord[];
}

// type ProductionVolumesApiResponse = Record<string, ProductionVolumeEntry[]>;

type ProductionVolumesApiResponse = {
  totatCount: number;
  results: Record<string, ProductionVolumeEntry[]>[];
};

export interface ProductionVolumesResponseConfig {
  applyScaling?: boolean;
  includeCumulatives?: boolean;
  includeRatios?: boolean;
  includeMbts?: boolean;
}

export interface UseProductionVolumesParams {
  filteredUwis: FilteredWell[];
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  responseConfig?: ProductionVolumesResponseConfig;
}

export const useProductionVolumes = ({
  filteredUwis,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  responseConfig,
}: UseProductionVolumesParams) => {
  const [wells, setWells] = useState<MultiWellProductionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const {
    applyScaling = true,
    includeCumulatives = true,
    includeRatios = true,
    includeMbts = true,
  } = responseConfig ?? {};

  useEffect(() => {
    if (
      !filteredUwis.length ||
      typeCurveHeaderDetailsId === null ||
      !dcaScenarioId
    )
      return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    const uwiStrings = filteredUwis.map((w) => w.uwi);
    const batches: string[][] = [];
    for (let i = 0; i < uwiStrings.length; i += PRODUCTION_VOLUMES_BATCH_SIZE) {
      batches.push(uwiStrings.slice(i, i + PRODUCTION_VOLUMES_BATCH_SIZE));
    }

    const apiConfig = {
      apply_scaling: applyScaling,
      include_cumulatives: includeCumulatives,
      include_ratios: includeRatios,
      include_mbts: includeMbts,
      useTcScenarioScaling:
        import.meta.env.VITE_TYPE_CURVE_SCENARIO_SCALING_ENABLED === 'true',
    };

    void (async () => {
      try {
        const batchResults = await Promise.all(
          batches.map((batchUwis) =>
            request.post<ProductionVolumesApiResponse, unknown>(
              PRODUCTION_VOLUMES_PATH,
              {
                query: {
                  productionVolumes: [
                    {
                      uwis: batchUwis,
                      version_ids: ['current'],
                    },
                  ],
                  type_curve_header_details_id: typeCurveHeaderDetailsId,
                  dca_scenario_id: dcaScenarioId,
                  response_config: apiConfig,
                },
              },
              controller
            )
          )
        );

        if (controller.signal.aborted) return;

        let merged: Record<string, ProductionVolumeEntry[]>[] = [];
        for (const result of batchResults) {
          if (result.type === 'abort') return;
          if (result.type === 'fatal') {
            setError(result.message ?? 'Request failed');
            setWells([]);
            return;
          }
          if (result.type === 'error') {
            setError('Failed to load production volumes');
            setWells([]);
            return;
          }
          if (result.type !== 'success') return;

          merged = [...merged, ...result.data.results];
        }

        const wellNameMap = new Map(filteredUwis.map((w) => [w.uwi, w.name]));
        const wellQualifierMap = new Map(
          filteredUwis.map((w) => [w.uwi, w.wellQualifier])
        );

        const productionData: MultiWellProductionData[] = merged.map(
          (entry) => {
            const uwi = Object.keys(entry)[0];
            const dataPoints = entry[uwi][0];

            return {
              uwi,
              name: wellNameMap.get(uwi),
              wellQualifier: wellQualifierMap.get(uwi),
              production: {
                volume: dataPoints?.volumes ?? [],
                cumulative: dataPoints?.cumulatives ?? [],
                ratio: dataPoints?.ratios ?? [],
                materialBalanceTime: dataPoints?.mbts ?? [],
              },
            };
          }
        );

        setWells(productionData);
      } catch {
        if (!controller.signal.aborted) {
          setError('Failed to load production volumes');
          setWells([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [
    filteredUwis,
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    applyScaling,
    includeCumulatives,
    includeRatios,
    includeMbts,
  ]);

  return { wells, isLoading, error };
};

// ─── Listing Export Hooks ───

/**
 * Starts an async listing export: posts the listing's own filter params, unmapped, plus the visible
 * column ids, through the shared `api` client. The server underscores request keys, so the payload
 * is the same object the listing posts and the export controller's permit stays the single owner of
 * the key list — a filter the table honours cannot be dropped on the way to the file.
 *
 * @param {string} path - The export endpoint, without a leading slash (the client sets `prefixUrl`).
 * @param {ExportParams | undefined} params - `queryParams` (the listing filters) and `visibleColumns`.
 * @return {Promise<{ exportId: string }>} The id the completion event is matched on.
 */
async function startListingExport(
  path: string,
  params?: ExportParams
): Promise<{ exportId: string }> {
  const qp = (params?.queryParams ?? {}) as TypeCurveHeadersQueryParams;
  const visibleColumns = (params?.visibleColumns as string[]) || [];

  try {
    // No client timeout: the controller resolves up to 9,999 ids through Elasticsearch before it
    // enqueues, and a timed-out start would drop the export id while the server still enqueues the
    // job, so its completion event would match nothing.
    const data = await api
      .post(path, {
        json: { query: { ...qp, visibleColumns } },
        timeout: false,
      })
      .json<{ exportId: string }>();
    return { exportId: data.exportId };
  } catch (error) {
    // The controller renders `{ error }` on failure; surface that text when it is there.
    if (error instanceof HTTPError) {
      const body = (await error.response.json().catch(() => ({}))) as {
        error?: string;
      };
      throw new Error(body.error || 'Failed to start export', { cause: error });
    }
    throw error;
  }
}

/**
 * The Excel export of the filtered Type Curves listing. The server resolves the same
 * type_curve_header_details rows the table shows (DetailsElastic), ordered like the table, and
 * builds the workbook in a job; the signed URL arrives over Pusher.
 *
 * @return {object} `startExport`, `isExporting` and `exportStatus`.
 */
export const useTypeCurveListingExport = () => {
  const exportFn = useCallback(
    (params?: ExportParams) =>
      startListingExport('queries/type_curve_header_details/exports', params),
    []
  );

  return useServerExport({
    channel: 'type_curve_exports',
    event: 'type_curve_export_complete',
    exportFn,
  });
};

// ─── Single Type Curve Export Hook ───
//
// Exports one Type Curve (the tc_public_id's current version). Async like the
// listing export: the workbook's Volumes tab derives from a DCA forecast, so it is
// built in a job and delivered over the shared Type Curve export channel (the
// exportId tells the two exports apart). See INS-11713.

export const useTypeCurveDetailExport = (tcPublicId: string) => {
  const exportFn = useCallback(async () => {
    const response = await fetch(
      `/type_curve_headers/${tcPublicId}/downloads/export`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token':
            document
              .querySelector('meta[name="csrf-token"]')
              ?.getAttribute('content') || '',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to start export');
    }

    const data = await response.json();
    return { exportId: data.exportId as string };
  }, [tcPublicId]);

  // autoDownload is off: useServerExport's default auto-download does
  // window.open(url) from a Pusher callback, which has no user activation and is
  // popup-blocked by Chrome/Safari. The single-TC button surfaces the finished
  // download as a clickable link instead (a real user click is never blocked).
  return useServerExport({
    channel: 'type_curve_exports',
    event: 'type_curve_export_complete',
    exportFn,
    autoDownload: false,
  });
};

/**
 * CSV export of the Inventories listing.
 *
 * Its own endpoint and Pusher channel, because the rows are zone-grained: the
 * file is flattened the same way the table is, one line per zone, so it cannot
 * share the Type Curves export.
 *
 * The filter payload matches the listing's, plus `visible_columns` in display
 * order. Asynchronous — the POST returns an exportId and the signed URL arrives
 * over Pusher.
 *
 * @return {object} `startExport`, `isExporting` and `exportStatus`.
 */
export const useInventoryListingExport = () => {
  const exportFn = useCallback(
    (params?: ExportParams) =>
      startListingExport('queries/inventory_workspaces/exports', params),
    []
  );

  return useServerExport({
    channel: 'inventory_workspace_exports',
    event: 'inventory_workspace_export_complete',
    exportFn,
  });
};

// ─── Aggregation Volumes Hook ───
//
// Fetches aggregation data (p10, p50, etc.) in a single batched call.
// Results are cached by aggregation type so selecting a previously-seen
// type never triggers a second network request.

const PRODUCTION_VOLUMES_AGGREGATIONS_PATH =
  '/queries/reservoir/production_volumes/aggregations';

// Maps the camelCase response keys (used as cache keys) to the snake_case
// request values expected by the backend.
const AGG_TYPE_TO_REQUEST_KEY: Record<string, string> = {
  arithmeticMean: 'arithmetic_mean',
  geometricMean: 'geometric_mean',
};

const toRequestKey = (type: string) => AGG_TYPE_TO_REQUEST_KEY[type] ?? type;

interface AggregationEntry {
  volumes?: TypeCurveVolumeRecord[];
  cumulatives?: TypeCurveVolumeRecord[];
  ratios?: TypeCurveRatioRecord[];
  mbts?: TypeCurveVolumeRecord[];
}

/** Well count per stream per month — sibling of wellAggregations in the response. */
export interface WellCountData {
  /** Volume streams: `{ month, oil, gas, water }` — count of contributing wells. */
  volumes: TypeCurveVolumeRecord[];
  /** Ratio streams: `{ month, gor, wor, wgr, cgr, watercut }` — count of contributing wells. */
  ratios: TypeCurveRatioRecord[];
}

interface AggregationResult {
  wellAggregations: Record<string, AggregationEntry>;
  wellCounts?: WellCountData;
}

interface AggregationApiResponse {
  totalCount: number;
  results: AggregationResult[];
}

// A Record<aggType, TypeCurveVolumes> — the "atom" that holds all fetched
// aggregation data indexed by aggregation type (p10, p50, etc.).
export type AggregationCache = Record<string, TypeCurveVolumes>;

export const useAggregationVolumes = ({
  filteredUwis,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  requestedTypes,
}: {
  filteredUwis: string[];
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  /** All aggregation types currently selected across all charts. */
  requestedTypes: string[];
}) => {
  // Cache: aggregation type → TypeCurveVolumes. Serves as the shared "atom"
  // for all fetched aggregation response data.
  const [aggregationCache, setAggregationCache] = useState<AggregationCache>(
    {}
  );
  // Well counts: independent of aggregation type, set once per params combination.
  const [wellCounts, setWellCounts] = useState<WellCountData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  // Tracks types already in-flight or successfully cached for the current
  // params combination, so we never issue a duplicate request.
  const fetchedTypesRef = useRef(new Set<string>());
  // When core params change (different TC / UWI set), the cache is stale.
  const prevParamsKeyRef = useRef('');

  useEffect(() => {
    const paramsKey = `${typeCurveHeaderDetailsId}::${dcaScenarioId}::${filteredUwis.join(
      ','
    )}`;

    if (prevParamsKeyRef.current !== paramsKey) {
      // Core params changed — invalidate cache and start fresh
      prevParamsKeyRef.current = paramsKey;
      fetchedTypesRef.current = new Set();
      setAggregationCache({});
      setWellCounts(null);
    }

    // Only fetch types that haven't been cached or are currently in-flight
    const typesToFetch = requestedTypes.filter(
      (t) => t !== 'none' && !fetchedTypesRef.current.has(t)
    );

    if (
      typesToFetch.length === 0 ||
      typeCurveHeaderDetailsId === null ||
      !dcaScenarioId ||
      filteredUwis.length === 0
    )
      return;

    // Mark these as in-flight immediately to prevent duplicate requests from
    // concurrent renders with the same requestedTypes value.
    for (const t of typesToFetch) fetchedTypesRef.current.add(t);

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    // Tracks whether this run successfully populated the cache. The effect
    // cleanup uses it to release the in-flight reservations on `fetchedTypesRef`
    // when the request never completes (e.g. aborted by an unrelated re-render
    // or component unmount), so the next effect run can re-fetch instead of
    // permanently believing the data is on its way.
    let completed = false;

    void (async () => {
      try {
        const response = await request.post<AggregationApiResponse, unknown>(
          PRODUCTION_VOLUMES_AGGREGATIONS_PATH,
          {
            query: {
              uwis: filteredUwis,
              type_curve_header_details_id: typeCurveHeaderDetailsId,
              dca_scenario_id: dcaScenarioId,
              response_config: {
                include_aggregations: typesToFetch.map(toRequestKey),
                apply_scaling: true,
                include_cumulatives: true,
                include_ratios: true,
                include_mbts: true,
                time_axis: 'month',
                include_well_counts: true,
                useTcScenarioScaling:
                  import.meta.env.VITE_TYPE_CURVE_SCENARIO_SCALING_ENABLED ===
                  'true',
              },
            },
          },
          controller
        );

        if (controller.signal.aborted || response.type === 'abort') return;

        if (response.type === 'fatal') {
          for (const t of typesToFetch) fetchedTypesRef.current.delete(t);
          setError(response.message ?? 'Request failed');
          return;
        }
        if (response.type === 'error') {
          for (const t of typesToFetch) fetchedTypesRef.current.delete(t);
          setError('Failed to load aggregation data');
          return;
        }
        if (response.type !== 'success') return;

        const result = response.data.results[0];
        const wellAggregations = result?.wellAggregations ?? {};

        // wellCounts is independent of aggregation type — capture it once
        if (result?.wellCounts) {
          setWellCounts(result.wellCounts);
        }

        setAggregationCache((prev) => {
          const next = { ...prev };
          for (const type of typesToFetch) {
            const entry = wellAggregations[type];
            if (entry) {
              next[type] = {
                volume: entry.volumes ?? [],
                cumulative: entry.cumulatives ?? [],
                ratio: entry.ratios ?? [],
                materialBalanceTime: entry.mbts ?? [],
              };
            }
          }
          return next;
        });
        completed = true;
      } catch {
        if (!controller.signal.aborted) {
          for (const t of typesToFetch) fetchedTypesRef.current.delete(t);
          setError('Failed to load aggregation data');
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
      // If the request was aborted before populating the cache (typical when
      // an unrelated dep churn or workspace navigation re-runs the effect),
      // release the in-flight reservations so the next run actually re-fetches
      // instead of short-circuiting on a stale `fetchedTypesRef` entry.
      if (!completed) {
        for (const t of typesToFetch) fetchedTypesRef.current.delete(t);
      }
    };
  }, [requestedTypes, filteredUwis, typeCurveHeaderDetailsId, dcaScenarioId]);

  return { aggregationCache, wellCounts, isLoading, error };
};

// ─── Type Curve Volumes Hook ───

const BULK_FORECASTS_PATH = '/queries/reservoir/bulk_forecasts';

interface BulkForecastEntry {
  typeCurveHeaderDetailsId: number;
  tcPublicId: string;
  uniqueTcId: string;
  typeCurveDcaForecastParameterIds: number[];
  volumes?: TypeCurveVolumeRecord[];
  cumulatives?: TypeCurveVolumeRecord[];
  ratios?: TypeCurveRatioRecord[];
  mbts?: TypeCurveVolumeRecord[];
  eur?: { oil: number; gas: number; water: number };
  eurRatios?: Record<string, number>;
  errors?: Array<{ code: string; message: string; details?: unknown }>;
}

interface BulkForecastsApiResponse {
  totalCount: number;
  results: Record<string, BulkForecastEntry[]>;
  errors?: Array<{ code: string; message: string; details?: unknown }>;
}

export interface TypeCurveVolumes {
  volume: TypeCurveVolumeRecord[];
  cumulative: TypeCurveVolumeRecord[];
  ratio: TypeCurveRatioRecord[];
  materialBalanceTime: TypeCurveVolumeRecord[];
}

export const useTypeCurveVolumes = ({
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  hasTypeCurveArea = true,
}: {
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  // Empty state gate. This call has no UWI/wells dependency, so it must be
  // suppressed explicitly when the header has no Type Curve Area. Defaults to
  // `true` so existing callers/tests are unaffected.
  hasTypeCurveArea?: boolean;
}) => {
  const [typeCurveData, setTypeCurveData] = useState<TypeCurveVolumes | null>(
    null
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!hasTypeCurveArea) {
      setTypeCurveData(null);
      return;
    }
    if (typeCurveHeaderDetailsId === null || !dcaScenarioId) return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    void (async () => {
      try {
        const response = await request.post<BulkForecastsApiResponse, unknown>(
          BULK_FORECASTS_PATH,
          {
            query: {
              bulkForecasts: {
                typeCurveHeaderDetailsIds: [typeCurveHeaderDetailsId],
                responseConfig: {
                  includeEur: true,
                  includeEurRatios: true,
                  includeCumulatives: true,
                  includeRatios: true,
                  includeMbts: true,
                },
              },
            },
          },
          controller
        );

        if (controller.signal.aborted) return;
        if (response.type === 'abort') return;
        if (response.type === 'fatal') {
          setError(response.message ?? 'Request failed');
          setTypeCurveData(null);
          return;
        }
        if (response.type === 'error') {
          setError('Failed to load type curve volumes');
          setTypeCurveData(null);
          return;
        }
        if (response.type !== 'success') return;

        const resultValues = Object.values(response.data.results);
        const entry = resultValues[0]?.[0] ?? null;

        setTypeCurveData(
          entry
            ? {
                volume: entry.volumes ?? [],
                cumulative: entry.cumulatives ?? [],
                ratio: entry.ratios ?? [],
                materialBalanceTime: entry.mbts ?? [],
              }
            : null
        );
      } catch {
        if (!controller.signal.aborted) {
          setError('Failed to load type curve volumes');
          setTypeCurveData(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [typeCurveHeaderDetailsId, dcaScenarioId, hasTypeCurveArea]);

  return { typeCurveData, isLoading, error };
};

// ─── Cross Plot Hook ───

const CROSS_PLOT_PATH = '/queries/cross_plots';
const CROSS_PLOT_BATCH_SIZE = 300;

/** Maps cross-plot axis keys to TypeCurveHeader field names for golden-line fallback. */
const CROSS_PLOT_AXIS_TO_HEADER_FIELD = {
  proppantIntensity: 'proppantIntensityLbsFt',
  fluidIntensityGal: 'fluidIntensityGalFt',
  fluidIntensityBbl: 'fluidIntensityBblFt',
  perfLateralLength: 'lateralLengthFt',
  closestInZoneHzDistanceFt: 'horizontalWellSpacingFt',
  secondClosestInZoneHzDistanceFt: 'horizontalWellSpacingFt',
  closestAnyZoneHzDistanceFt: 'horizontalWellSpacingFt',
  secondClosestAnyZoneHzDistanceFt: 'horizontalWellSpacingFt',
} as const;

export interface UseCrossPlotDataParams {
  filteredUwis: FilteredWell[];
  typeCurveHeaderDetailsId: number | null;
  dcaScenarioId: number;
  xAxis: string;
  yAxis: string;
  tcLateralLengthFt: number;
  typeCurveDateCreated: string;
  structureGridCollectionId?: number | null;
  /** TypeCurveHeader values for golden-line fallback. */
  tcHeaderValues?: {
    lateralLengthFt: number | null;
    proppantIntensityLbsFt: number | null;
    fluidIntensityBblFt: number | null;
    fluidIntensityGalFt: number | null;
    horizontalWellSpacingFt: number | null;
  };
}

export const useCrossPlotData = ({
  filteredUwis,
  typeCurveHeaderDetailsId,
  dcaScenarioId,
  xAxis,
  yAxis,
  tcLateralLengthFt,
  typeCurveDateCreated,
  structureGridCollectionId,
  tcHeaderValues,
}: UseCrossPlotDataParams) => {
  const [data, setData] = useState<CrossPlotData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (
      !filteredUwis.length ||
      typeCurveHeaderDetailsId === null ||
      !dcaScenarioId ||
      !xAxis ||
      !yAxis ||
      !tcLateralLengthFt ||
      !typeCurveDateCreated
    )
      return;

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    const uwiStrings = filteredUwis.map((w) => w.uwi);
    const batches: string[][] = [];
    for (let i = 0; i < uwiStrings.length; i += CROSS_PLOT_BATCH_SIZE) {
      batches.push(uwiStrings.slice(i, i + CROSS_PLOT_BATCH_SIZE));
    }

    void (async () => {
      try {
        const batchResults = await Promise.all(
          batches.map((batchUwis) =>
            request.post<CrossPlotApiResponse, unknown>(
              CROSS_PLOT_PATH,
              {
                query: {
                  x_axis: xAxis,
                  y_axis: yAxis,
                  uwis: batchUwis,
                  dca_scenario_id: dcaScenarioId,
                  tc_lateral_length_ft: tcLateralLengthFt,
                  type_curve_date_created: typeCurveDateCreated,
                  type_curve_header_details_id: typeCurveHeaderDetailsId,
                  use_elastic: true,
                  tc_mode: true,
                  use_tc_scenario_scaling:
                    import.meta.env.VITE_TYPE_CURVE_SCENARIO_SCALING_ENABLED ===
                    'true',
                  ...(structureGridCollectionId !== null &&
                    structureGridCollectionId !== undefined && {
                      structure_grid_collection_id: structureGridCollectionId,
                    }),
                },
              },
              controller
            )
          )
        );

        if (controller.signal.aborted) return;

        let mergedDataPoints: CrossPlotDataPoint[] = [];
        let lastResult: CrossPlotApiResponse['results'] | null = null;

        for (const result of batchResults) {
          if (result.type === 'abort') return;
          if (result.type === 'fatal') {
            setError(result.message ?? 'Request failed');
            setData(null);
            return;
          }
          if (result.type === 'error') {
            setError('Failed to load cross plot data');
            setData(null);
            return;
          }
          if (result.type !== 'success') return;

          lastResult = result.data.results;
          mergedDataPoints = [
            ...mergedDataPoints,
            ...result.data.results.dataPoints.map((dp) => ({
              uwi: dp.uwi,
              wellName: dp.wellName,
              wellQualifier: dp.wellQualifier,
              status: dp.status,
              x: dp.x,
              y: dp.y,
            })),
          ];
        }

        if (lastResult) {
          setData({
            xAxis: lastResult.xAxis,
            yAxis: lastResult.yAxis,
            xAxisLabel: lastResult.xAxisLabel,
            yAxisLabel: lastResult.yAxisLabel,
            xAxisValueType: lastResult.xAxisValueType ?? 'number',
            yAxisValueType: lastResult.yAxisValueType ?? 'number',
            dataPoints: mergedDataPoints,
            xMedian: lastResult.xMedian,
            yMedian: lastResult.yMedian,
            xTypeCurve: lastResult.tcX ?? null,
            yTypeCurve: lastResult.tcY ?? null,
            meta: {
              requestedWellCount: lastResult.meta.requestedWellCount,
              plottedWellCount: mergedDataPoints.length,
              excludedForNullAxis: lastResult.meta.excludedForNullAxis,
              excludedMissingFromIndex:
                lastResult.meta.excludedMissingFromIndex,
            },
          });
        }
      } catch {
        if (!controller.signal.aborted) {
          setError('Failed to load cross plot data');
          setData(null);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => {
      controller.abort();
    };
  }, [
    filteredUwis,
    typeCurveHeaderDetailsId,
    dcaScenarioId,
    xAxis,
    yAxis,
    tcLateralLengthFt,
    typeCurveDateCreated,
    structureGridCollectionId,
  ]);

  // Prefer TypeCurveHeader (details section) values for golden-line
  // references over API-computed tcX / tcY. The API still returns them
  // for future use, but header values take priority when available.
  const resolvedData = useMemo(() => {
    if (!data || !tcHeaderValues) return data;

    let { xTypeCurve, yTypeCurve } = data;
    let patched = false;

    const xField =
      CROSS_PLOT_AXIS_TO_HEADER_FIELD[
        xAxis as keyof typeof CROSS_PLOT_AXIS_TO_HEADER_FIELD
      ];
    if (xField) {
      const headerVal = tcHeaderValues[xField];
      if (headerVal !== null) {
        xTypeCurve = headerVal;
        patched = true;
      }
    }

    const yField =
      CROSS_PLOT_AXIS_TO_HEADER_FIELD[
        yAxis as keyof typeof CROSS_PLOT_AXIS_TO_HEADER_FIELD
      ];
    if (yField) {
      const headerVal = tcHeaderValues[yField];
      if (headerVal !== null) {
        yTypeCurve = headerVal;
        patched = true;
      }
    }

    return patched ? { ...data, xTypeCurve, yTypeCurve } : data;
  }, [data, tcHeaderValues, xAxis, yAxis]);

  return { data: resolvedData, isLoading, error };
};

// ─── Units (Development Areas) Table Hook ───
//
// Server-driven paginated table powered by `POST /queries/development_areas`.
// Backend is mocked via MSW for now (Epic 6a / INS-9519 backend extensions are
// in progress). Scoped only by spatial atoms (Global Map Filter) — not by DCA
// scenario, wells filters, or color-by rules.

const DEVELOPMENT_AREAS_PATH = '/queries/development_areas';

export const useDevelopmentAreasQuery = (typeCurveHeader: TypeCurveHeader) => {
  const mapFilterMode = useAtomValue(tcEffectiveMapFilterModeAtom);
  const mapBounds = useAtomValue(tcMapBoundsAtom);
  const aoiPolygon = useAtomValue(tcAoiPolygonAtom);
  const aoiLocked = useAtomValue(tcAoiPolygonLockedAtom);
  const unitFilters = useAtomValue(tcUnitFiltersAtom);
  const setUnitFilters = useSetAtom(tcUnitFiltersAtom);
  // Inventory Workspace only: the workspace id + selected formation name, sent so
  // the backend scopes units to the workspace's candidate units and the selected
  // formation's FOUCS overlay. Both `null` on plain Type Curve pages (params omitted).
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const highlightedFormation = useAtomValue(tcHighlightedFormationAtom);
  // On the Inventory Workspace the units query is scoped by workspace id +
  // selected formation (sent in the payload). Both come from atoms that settle
  // a moment after mount, so hold the fetch until they're ready — otherwise the
  // first request fires unscoped (no inventory_workspace_id / formation_name).
  // Keyed on the route so plain Type Curve pages (no inventory scope) still
  // fetch immediately.
  const isInventoryWorkspace =
    typeof window !== 'undefined' &&
    window.location.pathname.startsWith('/inventory_workspaces/');
  const inventoryContextReady =
    !isInventoryWorkspace ||
    (inventoryScope !== null && highlightedFormation !== null);
  const disabledUnitCategories = useAtomValue(
    tcDisabledUnitFilterCategoriesAtom
  );
  const setUnitsFacets = useSetAtom(tcUnitsFacetsAtom);
  const setUnitsTotalCount = useSetAtom(tcUnitsTotalCountAtom);
  const setSharedUnitsLoading = useSetAtom(tcUnitsLoadingAtom);
  const setFilteredUnits = useSetAtom(tcFilteredUnitsAtom);
  const setOverlappingUnits = useSetAtom(tcOverlappingUnitsAtom);
  const setUnitDetails = useSetAtom(tcUnitDetailsByIdAtom);
  const setResolutionPending = useSetAtom(tcUnitResolutionPendingAtom);

  const [units, setUnits] = useState<DevelopmentArea[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // Compute default unit filters from header (stable across renders)
  const defaultUnitFilters = useMemo<TcUnitFilters>(() => {
    const d: TcUnitFilters = { filedWellOnly: ['false'] };
    d.unitTypes = ['horizontal'];
    // Hide workspace-removed units by default. Seeds the unit-filters atom so it
    // flows into both the unit-table query and the map MVT tile params. Safe
    // outside a workspace — the backend's `removed` scope short-circuits when
    // there's no active workspace.
    d.removed = ['false'];
    return d;
  }, []);

  // Seed default unit filters into the atom on first mount (drives UI:
  // filter chips, modal state). The fetch itself uses effectiveFilters below
  // so it never waits for this async propagation.
  const hasSeededUnitFiltersRef = useRef(false);
  useEffect(() => {
    if (hasSeededUnitFiltersRef.current) return;
    if (Object.keys(unitFilters).length > 0) {
      hasSeededUnitFiltersRef.current = true;
      return;
    }
    hasSeededUnitFiltersRef.current = true;
    if (Object.keys(defaultUnitFilters).length > 0) {
      setUnitFilters(defaultUnitFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track whether the atom has ever been populated (by seeding or user).
  // Once true it stays true, so clearing all filters won't re-apply defaults.
  const hasBeenPopulatedRef = useRef(Object.keys(unitFilters).length > 0);
  if (Object.keys(unitFilters).length > 0) hasBeenPopulatedRef.current = true;

  // Before atom seeding propagates, use computed defaults so every render
  // (including the very first) produces correct filters for the fetch.
  // ...then drop any category the user switched off, so a disabled filter stops
  // narrowing the fetch while keeping its chips in the sidebar. Memoised because
  // this object is a fetchUnits dependency — a fresh identity every render would
  // re-arm the fetch effect and loop forever.
  const baseFilters = hasBeenPopulatedRef.current
    ? unitFilters
    : defaultUnitFilters;
  const effectiveFilters = useMemo(
    () =>
      omitDisabledCategories(
        baseFilters,
        disabledUnitCategories,
        TC_UNIT_FILTER_CATEGORIES
      ),
    [baseFilters, disabledUnitCategories]
  );

  const fetchUnits = useCallback(
    async (params: {
      page: number;
      pageSize: number;
      sorting?: SortingState;
      text?: string;
    }) => {
      // Hold the fetch until the Inventory Workspace scope + selected formation
      // are ready, so the request always carries inventory_workspace_id /
      // formation_name (no-op on plain Type Curve pages).
      if (!inventoryContextReady) return;
      // Empty state: no Type Curve Area on the header — make no units call and
      // clear shared unit results. (Units are not scoped by DCA/wells, so this
      // guard is required, not transitive.)
      if (typeCurveHeader.typeCurveAreaId === null) {
        setUnits([]);
        setTotalCount(0);
        setUnitsTotalCount(0);
        setUnitsFacets(null);
        setFilteredUnits([]);
        setOverlappingUnits([]);
        setIsLoading(false);
        setSharedUnitsLoading(false);
        return;
      }
      if (mapFilterMode === 'visible-extent' && !mapBounds) return;
      if (mapFilterMode === 'aoi-polygon' && (!aoiPolygon || !aoiLocked))
        return;

      if (abortRef.current) abortRef.current.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setIsLoading(true);
      setSharedUnitsLoading(true);
      setError(null);

      const sortCol = params.sorting?.[0];
      const sortBy = sortCol?.id;
      const sortOrder: 'asc' | 'desc' | undefined = sortCol
        ? sortCol.desc
          ? 'desc'
          : 'asc'
        : undefined;

      const payload = buildDevelopmentAreasPayload({
        typeCurveHeader,
        mapFilterMode,
        mapBounds,
        aoiPolygon,
        filters: effectiveFilters,
        typeCurveHeaderDetailsId: typeCurveHeader.id,
        inventoryWorkspaceId: inventoryScope?.inventoryWorkspaceId,
        formationName: highlightedFormation ?? undefined,
        page: params.page,
        pageSize: params.pageSize,
        sortBy,
        sortOrder,
        text: params.text || undefined,
      });

      try {
        const response = await request.post<
          DevelopmentAreasQueryResponse,
          unknown
        >(DEVELOPMENT_AREAS_PATH, { query: payload }, controller);

        if (controller.signal.aborted) return;
        if (response.type === 'abort') return;
        if (response.type === 'fatal') {
          setError(response.message ?? 'Request failed');
          setUnits([]);
          setTotalCount(0);
          setUnitsTotalCount(0);
          return;
        }
        if (response.type === 'error') {
          setError('Failed to load units');
          setUnits([]);
          setTotalCount(0);
          setUnitsTotalCount(0);
          return;
        }
        if (response.type !== 'success') return;

        const data = response.data;
        setUnits(data.results ?? []);
        setTotalCount(data.totalCount ?? 0);
        setUnitsTotalCount(data.totalCount ?? 0);
        if (data.facets) {
          setUnitsFacets(data.facets);
        }
        setFilteredUnits(data.filteredUnits ?? []);
        setOverlappingUnits(data.overlappingResults ?? []);
        // Merge lean unit details (acreage/unitName/unitType) for the
        // resolve-overlap drawer; accumulate across pages.
        // Kept as its own accumulating map (not derived from the units atom,
        // which only holds the current page) so the resolve-overlap drawer can
        // look up details for any unit loaded on an earlier page.
        const details: Record<number, TcUnitDetail> = {};
        for (const u of data.results ?? []) {
          details[u.id] = {
            name: u.name,
            unitName: u.unitName,
            unitType: u.unitType,
            acreage: u.acreage,
          };
        }
        setUnitDetails((prev) => ({ ...prev, ...details }));
      } catch {
        if (!controller.signal.aborted) {
          setError('Failed to load units');
          setUnits([]);
          setTotalCount(0);
          setUnitsTotalCount(0);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
          setSharedUnitsLoading(false);
          // Clear the resolution loader only when a request actually completes
          // (not when an earlier one is aborted by a newer fetch) — this is the
          // moment the map + overlap panel reflect the mutation. No-op outside a
          // resolution flow.
          setResolutionPending(false);
        }
      }
    },
    [
      typeCurveHeader,
      mapFilterMode,
      mapBounds,
      aoiPolygon,
      aoiLocked,
      effectiveFilters,
      inventoryScope?.inventoryWorkspaceId,
      highlightedFormation,
      inventoryContextReady,
      setUnitsFacets,
      setUnitsTotalCount,
      setSharedUnitsLoading,
      setFilteredUnits,
      setOverlappingUnits,
      setUnitDetails,
      setResolutionPending,
    ]
  );

  const loadData = useCallback(
    (params: TableState) => {
      void fetchUnits({
        page: params.page,
        pageSize: params.pageSize,
        sorting: params.sorting,
        text: params.globalFilter || undefined,
      });
    },
    [fetchUnits]
  );

  useEffect(() => {
    void fetchUnits({ page: 1, pageSize: 100 });
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchUnits]);

  // Story 3 — re-fetch (overlap recompute + removed flags) when an override
  // mutation bumps the refresh token. Skips the initial mount value.
  const unitResolutionRefresh = useAtomValue(tcUnitResolutionRefreshAtom);
  const skipInitialRefresh = useRef(true);
  useEffect(() => {
    if (skipInitialRefresh.current) {
      skipInitialRefresh.current = false;
      return;
    }
    // The resolution loader is cleared inside fetchUnits' own finally (only when
    // the request isn't aborted), so it stays on until the map + panel actually
    // reflect the change — not when an interim request gets superseded.
    void fetchUnits({ page: 1, pageSize: 100 });
  }, [unitResolutionRefresh, fetchUnits]);

  return { units, totalCount, isLoading, error, loadData };
};

// ─── Formation Slots (Inventory Management) Table Hook ───
//
// Server-driven paginated table powered by
// `POST /queries/development_area_formation_slots`. Backend is mocked via MSW.
// Per the spec the slot table IGNORES left-panel filtering (Global Map Filter,
// Wells filters, DCA Scenario, Units accordion). It is scoped only by the
// currently-active development_area_id provided by the Inventory Management
// chart's unit navigator (teammate-owned atom; hardcoded for now in the page).

const FORMATION_SLOTS_PATH = '/queries/development_area_formation_slots';

export const useFormationSlotsQuery = (
  typeCurveHeaderDetailsId: number,
  collectionIds?: number[],
  // Both the Type Curve pages and the Inventory Workspace source the slot table
  // from this per-unit query. Kept as a parameter so a caller can still gate it.
  enabled = true
) => {
  const selectedDevArea = useAtomValue(tcSelectedDevAreaAtom);
  const ltdRefreshTrigger = useAtomValue(ltdRefreshTriggerAtom);
  // On the Inventory Workspace page this is set to { inventoryWorkspaceId, ... }; on plain Type
  // Curve pages it is null, so inventory_workspace_id is only sent under inventory scope.
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  // Inventory Workspace only: the selected leaf's formation. Null on plain Type
  // Curve pages, so formation_name is only sent under inventory scope.
  const highlightedFormation = useAtomValue(tcHighlightedFormationAtom);
  // Stable dep key for the origin-area package id array, so a fresh array each
  // render doesn't re-fire the fetch.
  const originPackageIdsKey =
    inventoryScope?.inventoryFormationPackageIds?.join(',') ?? '';
  // Same guard for the collection ids: callers pass an inline array, so keying
  // the fetch on its identity would re-fire on every parent render (e.g. each
  // Unit Builder selection). Key on the value instead.
  const collectionIdsKey = collectionIds?.join(',') ?? '';
  // Clones carry a synthetic negative id no endpoint can resolve, so they read
  // their source's slots — the same substitution the gun barrel query makes, and
  // for the same reason: a fresh clone occupies the same ground.
  const developmentAreaId =
    selectedDevArea?.sourceDevelopmentAreaId ??
    selectedDevArea?.developmentAreaId ??
    null;
  const crossSectionEndPoints = selectedDevArea?.crossSectionEndPoints;
  // Signature of everything the fetch scopes on (+ the LTD refresh). Re-fires the
  // fetch effect when any scoped value changes.
  const slotsSignature = [
    developmentAreaId,
    typeCurveHeaderDetailsId,
    inventoryScope?.inventoryWorkspaceId ?? '',
    originPackageIdsKey,
    highlightedFormation ?? '',
    collectionIdsKey,
    crossSectionEndPoints ? JSON.stringify(crossSectionEndPoints) : '',
    ltdRefreshTrigger,
  ].join('|');
  // On the Inventory Workspace the slot table is surface-owned
  // (`slotTableStateAtoms[surface]` via `useUnitFormationSlotsFor`) so it isn't
  // re-POSTed as the Unit Builder remounts this table on open/close. Read this
  // surface's result and skip this component's own fetch. The plain Type Curve
  // page (no provider → 'main', inventoryScope null) fetches here.
  const surface = useSurface();
  const pageOwnedSlots = useAtomValue(slotTableStateAtoms[surface]);
  const [slots, setSlots] = useState<FormationSlot[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  // Keep a ref so fetchSlots always reads the latest value without
  // recreating the callback when the selection atom settles in stages
  // (id first, then endpoints), which previously caused abort→re-fetch.
  const crossSectionRef = useRef(crossSectionEndPoints);
  crossSectionRef.current = crossSectionEndPoints;

  // Content key for the cross-section line. Moving the slider (or rotating)
  // changes the endpoints while the development area id stays the same, so we
  // key the fetch on this — mirroring the Gun Barrel query, which both scope on
  // crossSectionEndPoints. Stringified so it re-fires by value, not identity.
  const crossSectionKey = crossSectionEndPoints
    ? JSON.stringify(crossSectionEndPoints)
    : null;

  const fetchSlots = useCallback(async () => {
    // Inventory surfaces are surface-owned — read `slotTableStateAtoms[surface]`
    // instead (handled below); this component's own fetch is a no-op there.
    if (inventoryScope) return;
    if (!enabled || developmentAreaId === null) {
      setSlots([]);
      setTotalCount(0);
      setIsLoading(false);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    setError(null);

    // Type-Curve-page path only — inventory surfaces returned above (page-owned),
    // so no inventory scope / formation fields here.
    const payload = buildFormationSlotsPayload({
      developmentAreaId,
      typeCurveHeaderDetailsId,
      crossSectionEndPoints: crossSectionRef.current,
      collectionIds,
    });

    try {
      const response = await request.post<FormationSlotsQueryResponse, unknown>(
        FORMATION_SLOTS_PATH,
        { query: payload },
        controller
      );

      if (controller.signal.aborted) return;
      if (response.type === 'abort') return;
      if (response.type === 'fatal') {
        setError(response.message ?? 'Request failed');
        setSlots([]);
        setTotalCount(0);
        return;
      }
      if (response.type === 'error') {
        setError('Failed to load slots');
        setSlots([]);
        setTotalCount(0);
        return;
      }
      if (response.type !== 'success') return;

      const data = response.data;
      setSlots(data.results ?? []);
      setTotalCount(data.totalCount ?? 0);
    } catch {
      if (!controller.signal.aborted) {
        setError('Failed to load slots');
        setSlots([]);
        setTotalCount(0);
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
    // crossSectionKey re-fires the fetch when the section line moves; the value
    // itself is read from crossSectionRef.current inside.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    developmentAreaId,
    typeCurveHeaderDetailsId,
    inventoryScope?.inventoryWorkspaceId,
    // Stable string keys, not the arrays' identity (see originPackageIdsKey /
    // collectionIdsKey) — a fresh-but-equal array must not re-fire the fetch.
    originPackageIdsKey,
    highlightedFormation,
    collectionIdsKey,
    crossSectionKey,
    slotsSignature,
    enabled,
  ]);

  useEffect(() => {
    // Slot table shows all rows in one go (no server-side pagination). Search /
    // sort / pagination controls are handled client-side by the DataTable.
    void fetchSlots();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchSlots, ltdRefreshTrigger]);

  // Inventory surfaces read the page-owned result; the Type Curve page uses this
  // component's own fetch state.
  if (inventoryScope) {
    return {
      slots: pageOwnedSlots?.slots ?? [],
      totalCount: pageOwnedSlots?.totalCount ?? 0,
      // Only report loading when there are no rows to show yet — during a refetch
      // the previous rows are retained, so keep rendering them (no placeholder
      // flash), matching the map.
      isLoading:
        (pageOwnedSlots?.loading ?? true) && !pageOwnedSlots?.slots?.length,
      // Raw in-flight flag (true even during a refetch that keeps its rows), so
      // the table can show a non-blocking "refreshing" overlay when the section
      // line moves / a re-slot lands, without blanking the rows.
      isFetching: pageOwnedSlots?.loading ?? false,
      error: pageOwnedSlots?.error ?? null,
    };
  }

  return { slots, totalCount, isLoading, isFetching: isLoading, error };
};

// ─── Inventory Verification Hook ────────────────────────────────────────────
//
// Fetches unit verification data when a workspace is active and populates
// `unitVerificationsAtom` keyed by unit name for consumption by the map layer
// and gun barrel panel.

export const useInventoryVerifications = () => {
  const { id: typeCurveId } = useParams<{ id: string }>();
  const effectiveWorkspaceId = useAtomValue(tcEffectiveWorkspaceIdAtom);
  const setUnitVerifications = useSetAtom(unitVerificationsAtom);
  const abortRef = useRef<AbortController | null>(null);

  const fetchVerifications = useCallback(async () => {
    if (!typeCurveId || !effectiveWorkspaceId) {
      setUnitVerifications(new Map());
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const url = `/type_curve_workspaces/${effectiveWorkspaceId}/header/units`;

    try {
      const response = await request.get<UnitVerificationsResponse>(
        url,
        controller
      );

      if (controller.signal.aborted) return;
      if ('error' in response) return;

      const map = new Map<string, UnitVerification>();
      for (const unit of response.units) {
        map.set(unit.name, unit);
      }
      setUnitVerifications(map);
    } catch {
      if (!controller.signal.aborted) {
        setUnitVerifications(new Map());
      }
    }
  }, [typeCurveId, effectiveWorkspaceId, setUnitVerifications]);

  useEffect(() => {
    void fetchVerifications();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchVerifications]);

  return { refetch: fetchVerifications };
};

// ─── Inventory Verification Summary Hook ────────────────────────────────────
//
// Fetches the aggregated inventory verification summary from
// `GET /type_curves/workspaces/:id/unit_verifications/summary` and populates
// `inventoryVerificationSummaryAtom` for consumption by the tasks panel.

export const useInventoryVerificationSummary = () => {
  const { id: typeCurveId } = useParams<{ id: string }>();
  const activeWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const setSummary = useSetAtom(inventoryVerificationSummaryAtom);
  const abortRef = useRef<AbortController | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!typeCurveId || !activeWorkspace?.id) {
      setSummary(null);
      return;
    }

    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    const url = `/type_curve_workspaces/${activeWorkspace.id}/header/unit_verifications/summary`;

    try {
      const response = await request.get<InventoryVerificationSummary>(
        url,
        controller
      );

      if (controller.signal.aborted) return;
      if ('error' in response) return;
      setSummary(response);
    } catch {
      if (!controller.signal.aborted) setSummary(null);
    }
  }, [typeCurveId, activeWorkspace?.id, setSummary]);

  useEffect(() => {
    void fetchSummary();
    return () => {
      abortRef.current?.abort();
    };
  }, [fetchSummary]);

  return { refetch: fetchSummary };
};

// ─── Promoted-context defaults ───────────────────────────────────────────────
//
// Some page defaults only make sense once a Type Curve exists for real. A blank
// (unpromoted) workspace starts with no Well Qualifier colour rule and with
// Summary Table / DCA Parameters unchecked. No active workspace means a plain
// Type Curve page, which is promoted by definition and keeps the old defaults.
//
// Keyed on workspace id + promoted so it runs on transitions only, leaving the
// user's own checkbox changes alone in between.

export const usePromotedContextDefaults = () => {
  const activeWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const inventoryScope = useAtomValue(tcInventoryGunBarrelScopeAtom);
  const setColorByRules = useSetAtom(tcColorByRulesAtom);
  const setVisibility = useSetAtom(pageSectionVisibilityAtom);

  const isPromoted = activeWorkspace ? activeWorkspace.promoted : true;
  // The default Well Qualifier rule is a Type-Curve-page default; the Inventory
  // Workspace has NO qualifier colouring in any case. Gate injection on the
  // inventory scope so plain Type Curve pages stay untouched. Keyed on the
  // scope's null-ness (a boolean), not the object, so selecting a different zone
  // does not re-run this.
  const isInventoryWorkspace = inventoryScope !== null;
  const injectQualifier = isPromoted && !isInventoryWorkspace;
  const contextKey = `${
    activeWorkspace?.id ?? 'none'
  }:${isPromoted}:${isInventoryWorkspace}`;
  const appliedForRef = useRef<string | null>(null);

  useEffect(() => {
    if (appliedForRef.current === contextKey) return;
    appliedForRef.current = contextKey;

    setColorByRules((prev) => {
      const has = prev.some(
        (r) => r.ruleId === DEFAULT_WELL_QUALIFIER_RULE.ruleId
      );
      if (injectQualifier) {
        return has ? prev : [...prev, DEFAULT_WELL_QUALIFIER_RULE];
      }
      return has
        ? prev.filter((r) => r.ruleId !== DEFAULT_WELL_QUALIFIER_RULE.ruleId)
        : prev;
    });

    // Only ever forces them OFF. A promoted context is left alone so this never
    // overrides the stored default or a choice the user made themselves, and the
    // existing no-Type-Curve-Area gate keeps working untouched on top of this.
    if (isPromoted) return;
    setVisibility((prev) => {
      return !prev.summaryTable && !prev.dcaParameters
        ? prev
        : { ...prev, summaryTable: false, dcaParameters: false };
    });
  }, [contextKey, isPromoted, injectQualifier, setColorByRules, setVisibility]);
};

// ─── Auto Verification Status Color-By Hook ─────────────────────────────────
//
// Automatically injects a default verification-status color-by rule into the
// unit color-by atoms when a workspace is entered, and removes it on exit.
// Populates the name map from client-side verification data (no extra API calls).
//
// The name map is the authoritative source for verification status coloring —
// it's built from `unitVerificationsAtom` (client-side data fetched by
// `useInventoryVerifications`). If the color-by modal's Apply action overwrites
// the name map, Effect 2 detects the stale entries and re-populates them.

export const useAutoVerificationStatusColorBy = () => {
  const activeWorkspace = useAtomValue(activeTcWorkspaceAtom);
  const latestPromotedWorkspaceId = useAtomValue(latestPromotedWorkspaceIdAtom);
  const unitVerifications = useAtomValue(unitVerificationsAtom);
  const setUnitColorByRules = useSetAtom(tcUnitColorByRulesAtom);
  const unitColorByNameMap = useAtomValue(tcUnitColorByNameMapAtom);
  const setUnitColorByNameMap = useSetAtom(tcUnitColorByNameMapAtom);
  const prevWorkspaceIdRef = useRef<number | null>(null);

  // Effect 0 — Defensive cleanup: ensure no verification color-by data persists
  // when there's no effective workspace (active or promoted). Covers edge cases
  // where atoms retain stale data across component remounts (Jotai singleton
  // provider keeps atom values alive app-wide, but component refs reset).
  useEffect(() => {
    if (activeWorkspace || latestPromotedWorkspaceId) return;

    setUnitColorByRules((prev) => {
      const filtered = prev.filter(
        (r) => r.ruleId !== DEFAULT_VERIFICATION_STATUS_RULE_ID
      );
      return filtered.length !== prev.length ? filtered : prev;
    });
    setUnitColorByNameMap((prev) => {
      const hasVerification = Object.keys(prev).some((k) =>
        k.startsWith('verificationStatus:')
      );
      if (!hasVerification) return prev;
      const next: Record<string, string[]> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (!key.startsWith('verificationStatus:')) {
          next[key] = value;
        }
      }
      return next;
    });
  }, [
    activeWorkspace,
    latestPromotedWorkspaceId,
    setUnitColorByRules,
    setUnitColorByNameMap,
  ]);

  // Effect 1 — Workspace enter/exit: inject or remove the default rule.
  useEffect(() => {
    const currentId = activeWorkspace?.id ?? null;
    const prevId = prevWorkspaceIdRef.current;
    prevWorkspaceIdRef.current = currentId;

    // Entering a workspace injects the rule only once the Type Curve is
    // promoted — a blank workspace starts with no colour rule.
    if (currentId !== null && prevId === null && activeWorkspace?.promoted) {
      // Entering a workspace — inject rule if no verificationStatus rule exists
      setUnitColorByRules((prev) => {
        if (prev.some((r) => r.attribute === 'verificationStatus')) {
          return prev;
        }
        return [...prev, DEFAULT_VERIFICATION_STATUS_RULE];
      });
    } else if (currentId === null && prevId !== null) {
      // Leaving a workspace — keep rule and name map when a promoted
      // workspace exists so verification color-by continues to work
      // outside the workspace (facets still carry verification data).
      if (!latestPromotedWorkspaceId) {
        setUnitColorByRules((prev) =>
          prev.filter((r) => r.ruleId !== DEFAULT_VERIFICATION_STATUS_RULE_ID)
        );
        setUnitColorByNameMap((prev) => {
          const next: Record<string, string[]> = {};
          for (const [key, value] of Object.entries(prev)) {
            if (!key.startsWith('verificationStatus:')) {
              next[key] = value;
            }
          }
          return next;
        });
      }
    } else if (currentId !== null && prevId !== null && currentId !== prevId) {
      // Switching workspaces — clear name map entries (they'll repopulate
      // from the new workspace's verification data)
      setUnitColorByNameMap((prev) => {
        const next: Record<string, string[]> = {};
        for (const [key, value] of Object.entries(prev)) {
          if (!key.startsWith('verificationStatus:')) {
            next[key] = value;
          }
        }
        return next;
      });
    }
  }, [
    activeWorkspace?.id,
    activeWorkspace?.promoted,
    latestPromotedWorkspaceId,
    setUnitColorByRules,
    setUnitColorByNameMap,
  ]);

  // Effect 2 — Name map population: build from client-side verification data.
  // Watches both `unitVerificationsAtom` (source of truth) and
  // `unitColorByNameMap` (target). If the verification entries in the name map
  // are stale or missing (e.g., overwritten by the color-by modal's Apply),
  // this effect re-populates them from the authoritative verification data.
  useEffect(() => {
    if (unitVerifications.size === 0) return;
    // Don't populate name map when there's no effective workspace —
    // unitVerifications may hold stale data before useInventoryVerifications
    // clears it in the same render cycle.
    if (!activeWorkspace && !latestPromotedWorkspaceId) return;

    const correctNames: string[] = [];
    const incorrectNames: string[] = [];

    for (const [unitName, verification] of unitVerifications) {
      if (verification.inventoryVerification.status === true) {
        correctNames.push(unitName);
      } else if (verification.inventoryVerification.status === false) {
        incorrectNames.push(unitName);
      }
    }

    // Check if the current name map already has the correct entries.
    // Skip the write if nothing changed to avoid render loops.
    const existingCorrect = unitColorByNameMap['verificationStatus:correct'];
    const existingIncorrect =
      unitColorByNameMap['verificationStatus:incorrect'];

    const correctMatch =
      correctNames.length === (existingCorrect?.length ?? 0) &&
      correctNames.every((n, i) => existingCorrect?.[i] === n);
    const incorrectMatch =
      incorrectNames.length === (existingIncorrect?.length ?? 0) &&
      incorrectNames.every((n, i) => existingIncorrect?.[i] === n);

    if (correctMatch && incorrectMatch) return;

    setUnitColorByNameMap((prev) => {
      // Preserve non-verificationStatus entries
      const next: Record<string, string[]> = {};
      for (const [key, value] of Object.entries(prev)) {
        if (!key.startsWith('verificationStatus:')) {
          next[key] = value;
        }
      }
      if (correctNames.length > 0) {
        next['verificationStatus:correct'] = correctNames;
      }
      if (incorrectNames.length > 0) {
        next['verificationStatus:incorrect'] = incorrectNames;
      }
      return next;
    });
  }, [
    unitVerifications,
    unitColorByNameMap,
    setUnitColorByNameMap,
    activeWorkspace,
    latestPromotedWorkspaceId,
  ]);
};

// ---------------------------------------------------------------------------
// Type Curve spatial (GIS) export (INS-13270)
// ---------------------------------------------------------------------------

/**
 * The Type Curve spatial-export request body: the chosen formats, the current Type Curves catalog
 * filter params (scoping the export to exactly what the table shows), and the chosen attribute ids
 * ([] = geometry-only).
 *
 * Intersected with `ExportParams` so it carries that type's `[key: string]: unknown` index signature
 * and is assignable to `startExport`'s parameter both ways (a plain object type is not — that mismatch
 * was failing the `types` CI job).
 */
// The Type Curve spatial export submits the shared single-layer request shape (formats + filters +
// attributes). Aliased, not redefined, so the single-layer surfaces can't drift.
export type TypeCurveSpatialExportRequest = SingleLayerSpatialExportRequest;

/**
 * Owns the Type Curves page spatial (GIS) export: POSTs the selected formats + attributes + current
 * filters to the Type Curve spatial endpoint and tracks completion over Pusher (auto-downloading the
 * bundle), through the shared useServerExport flow. Mirrors useAreasSpatialExport.
 *
 * @return {object} The server-export status + `startExport(request)` + the signed-in user id.
 */
export const useTypeCurveSpatialExport = () => {
  const exportFn = useCallback(
    (params?: ExportParams) =>
      postSpatialExport('queries/type_curve_headers/spatial_export', params),
    []
  );

  return useSpatialExport<TypeCurveSpatialExportRequest>({
    event: 'type_curve_headers_spatial_export_complete',
    exportFn,
  });
};

export type TypeCurveSpatialExport = ReturnType<
  typeof useTypeCurveSpatialExport
>;
